import React, { useCallback, useMemo, useState } from 'react'
import { CheckCircle2, ExternalLink, Loader2 } from 'lucide-react'
import { Link } from 'react-router-dom'

import { nutrimaticLabels } from '@/nutrimatic'
import { cn } from '@/lib/utils'
import { Button } from '@/ui/button'
import { WalletActionButton } from '@/ui/wallet-action-button'
import { WalletActionWaitPanel } from '@/ui/wallet-action-wait-panel'
import { useWalletActionButton } from '../../../../hooks/useWalletActionButton'
import { BurnField as Field } from '../../../../domain/liquidity'
import type { RemoveLiquidityControllerResult } from '../../../../features/liquidity/useRemoveLiquidityController'
import { ApprovalState } from '../../../../hooks/useApproveCallback'
import { useAppWeb3 } from '../../../../hooks'
import { getScanLink } from '../../../../utils'
import { MobileSwapScreenHeader } from '../../../swap/nutrimatic/mobile/SwapNutrimaticMobileSimpleViews'
import { NutrimaticMobileNotice } from '../../../swap/nutrimatic/mobile/NutrimaticMobileNotice'
import { applyAmountKeypadKey, MobileAmountKeypad } from '../../../swap/nutrimatic/mobile/MobileAmountKeypad'
import { MobileFluidAmount } from '../../../swap/nutrimatic/mobile/MobileFluidAmount'
import { NutrimaticMobileCurrencyLeg } from './NutrimaticMobileCurrencyLeg'
import { MobileRemoveLiquidityMetricsPanel, MobileRemoveLiquidityReviewDetails } from './MobileRemoveLiquidityQuoteBlocks'
import { useRemoveLiquidityMobileNotice } from './useLiquidityMobileNotice'
import { useLiquidityRemoveNutrimaticFlow } from '../useLiquidityRemoveNutrimaticFlow'
import {
  isLiquidityWalletWaitActive,
  LiquidityNutrimaticMobileReviewWait,
} from './LiquidityNutrimaticMobileReviewWait'
import { LiquidityNutrimaticMobileOutcomeView } from './LiquidityNutrimaticMobileOutcomeView'
import {
  liquidityNutrimaticTabletHeaderProps,
  useLiquidityHubTabletBack,
} from '../useLiquidityHubTabletBack'

const REMOVE_PRESETS = [0.25, 0.5, 0.75, 1] as const
type NutrimaticVariant = 'mobile' | 'desktop-tablet'

export function LiquidityNutrimaticMobileRemoveReviewView({
  controller,
  variant = 'mobile',
}: {
  controller: RemoveLiquidityControllerResult
  variant?: NutrimaticVariant
}) {
  const backToPositions = useLiquidityHubTabletBack()
  const {
    attemptingTxn,
    cancelLiquidityWalletWait,
    confirmRemove,
    currencyA,
    currencyB,
    formattedAmounts,
    handleReviewDismiss,
    walletProgressPhase,
  } = controller

  const [submitting, setSubmitting] = useState(false)
  const isProcessing = isLiquidityWalletWaitActive({
    attemptingTxn,
    walletProgressPhase,
  })

  const onConfirm = () => {
    setSubmitting(true)
    void Promise.resolve(confirmRemove()).finally(() => setSubmitting(false))
  }

  return (
    <div className="nm-mobile-simple__review-stage flex min-h-0 flex-1 flex-col overflow-hidden">
      <MobileSwapScreenHeader
        title={nutrimaticLabels.mobileSimple.reviewScreenTitle}
        {...liquidityNutrimaticTabletHeaderProps(variant, backToPositions, {
          onBack: handleReviewDismiss,
          backLabel: nutrimaticLabels.cancel,
          hideBack: isProcessing,
        })}
      />

      <div className="nm-mobile-simple__body nm-mobile-simple__body--scroll" data-mobile-scroll-container>
        <p className="nm-mobile-simple__review-kicker">{nutrimaticLabels.mobileSimple.youWillReceive}</p>
        <p className="nm-mobile-simple__review-hero">
          {formattedAmounts[Field.CURRENCY_A]} {currencyA?.symbol} + {formattedAmounts[Field.CURRENCY_B]}{' '}
          {currencyB?.symbol}
        </p>

        <LiquidityNutrimaticMobileReviewWait
          attemptingTxn={attemptingTxn}
          walletProgressPhase={walletProgressPhase}
          onCancelWallet={cancelLiquidityWalletWait}
          reviewDetails={<MobileRemoveLiquidityReviewDetails controller={controller} />}
        />
      </div>

      {!isProcessing ? (
        <div className="nm-mobile-simple__footer">
          <Button
            type="button"
            className="nm-mobile-simple__cta gap-2"
            disabled={submitting}
            onClick={onConfirm}
          >
            {submitting ? <Loader2 className="size-5 animate-spin" aria-hidden /> : null}
            {submitting
              ? nutrimaticLabels.transactionSigning
              : nutrimaticLabels.mobileSimple.confirmRemove}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

export function LiquidityNutrimaticMobileRemoveCancelledView({
  controller,
}: {
  controller: RemoveLiquidityControllerResult
}) {
  const { dismissCancelledLiquidity, walletErrorMessage } = controller

  return (
    <LiquidityNutrimaticMobileOutcomeView
      kind="cancelled"
      operation="remove"
      detail={walletErrorMessage}
      onBackToForm={dismissCancelledLiquidity}
    />
  )
}

export function LiquidityNutrimaticMobileRemoveErrorView({
  controller,
}: {
  controller: RemoveLiquidityControllerResult
}) {
  const { dismissCancelledLiquidity, retryLiquidityAfterError, walletErrorMessage } = controller

  return (
    <LiquidityNutrimaticMobileOutcomeView
      kind="error"
      operation="remove"
      detail={walletErrorMessage}
      onBackToForm={dismissCancelledLiquidity}
      onRetry={retryLiquidityAfterError}
    />
  )
}

export function LiquidityNutrimaticMobileRemoveSuccessView({
  controller,
}: {
  controller: RemoveLiquidityControllerResult
}) {
  const { chainId } = useAppWeb3()
  const { currencyA, currencyB, handleDismissConfirmation, txHash } = controller
  const explorerHref = chainId && txHash ? getScanLink(chainId, txHash, 'transaction') : undefined

  return (
    <>
      <div className="nm-mobile-simple__success">
        <span className="nm-mobile-simple__hero-brand nm-mobile-simple__success-brand">
          {nutrimaticLabels.mobileSimple.productBrand}
        </span>
        <div className="nm-mobile-simple__success-icon">
          <CheckCircle2 className="size-9" aria-hidden />
        </div>
        <h2 className="nm-mobile-simple__success-title">
          {nutrimaticLabels.mobileSimple.removeSuccessTitle(
            currencyA?.symbol ?? '',
            currencyB?.symbol ?? '',
          )}
        </h2>
        {txHash ? <p className="nm-mobile-simple__hash-caption mt-4">{txHash}</p> : null}
        <div className="nm-mobile-simple__success-links">
          {explorerHref ? (
            <a
              href={explorerHref}
              target="_blank"
              rel="noopener noreferrer"
              className="nm-mobile-simple__success-link"
            >
              <ExternalLink className="size-4" aria-hidden />
              {nutrimaticLabels.mobileSimple.viewExplorer}
            </a>
          ) : null}
          <Link to="/portfolio/movements" className="nm-mobile-simple__success-link">
            <ExternalLink className="size-4" aria-hidden />
            {nutrimaticLabels.mobileSimple.viewActivity}
          </Link>
        </div>
      </div>
      <div className="nm-mobile-simple__footer nm-mobile-simple__footer--stacked">
        <Button type="button" className="nm-mobile-simple__cta" onClick={handleDismissConfirmation}>
          {nutrimaticLabels.mobileSimple.newLiquidityAction}
        </Button>
      </div>
    </>
  )
}

export function LiquidityNutrimaticMobileRemoveFormView({
  controller,
  variant = 'mobile',
}: {
  controller: RemoveLiquidityControllerResult
  variant?: NutrimaticVariant
}) {
  const isDesktopTablet = variant === 'desktop-tablet'
  const backToPositions = useLiquidityHubTabletBack()
  const { account } = useAppWeb3()
  const {
    approval,
    currencyA,
    currencyB,
    error,
    formattedAmounts,
    isValid,
    onAttemptToApprove,
    onUserInput,
    openConfirm,
    openConnectModal,
    parsedAmounts,
    signatureData,
  } = controller

  const notice = useRemoveLiquidityMobileNotice(controller)
  const percentValue = formattedAmounts[Field.LIQUIDITY_PERCENT]

  const applyKeypad = useCallback(
    (key: string) => {
      const base = percentValue === '<1' ? '' : percentValue.replace('%', '')
      const next = applyAmountKeypadKey(base, key)
      if (next === '') {
        onUserInput(Field.LIQUIDITY_PERCENT, '0')
        return
      }
      const parsed = Number.parseInt(next, 10)
      if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= 100) {
        onUserInput(Field.LIQUIDITY_PERCENT, String(parsed))
      }
    },
    [onUserInput, percentValue],
  )

  const applyPercentInput = useCallback(
    (raw: string) => {
      const digits = raw.replace(/[^\d]/g, '')
      if (digits === '') {
        onUserInput(Field.LIQUIDITY_PERCENT, '0')
        return
      }
      const parsed = Number.parseInt(digits, 10)
      if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= 100) {
        onUserInput(Field.LIQUIDITY_PERCENT, String(parsed))
      }
    },
    [onUserInput],
  )

  const approved = approval === ApprovalState.APPROVED || signatureData !== null
  const hasAmounts = Boolean(parsedAmounts[Field.CURRENCY_A] && parsedAmounts[Field.CURRENCY_B])
  const needsApproval = isValid && !approved

  const {
    label: approvePrimaryLabel,
    phase: approvePrimaryPhase,
    isBusy: approvePrimaryBusy,
    canCancel: canCancelApprovePrimary,
    disabled: approvePrimaryDisabled,
    run: runApprovePrimary,
    cancel: cancelApprovePrimary,
  } = useWalletActionButton({
    onAction: () => onAttemptToApprove(),
    pendingOnChain: approval === ApprovalState.PENDING,
    labels: useMemo(
      () => ({ idle: nutrimaticLabels.mobileSimple.approveToken('LP') }),
      [],
    ),
    disabledWhenIdle: !needsApproval,
  })

  const primaryLabel = !account
    ? nutrimaticLabels.mobileSimple.connectWallet
    : needsApproval
      ? approvePrimaryLabel
      : nutrimaticLabels.mobileSimple.reviewRemoveLiquidity

  const primaryDisabled =
    !account ? false : needsApproval ? approvePrimaryDisabled : !isValid || !hasAmounts

  const primaryLoading = Boolean(account && needsApproval && approvePrimaryBusy)
  const showApproveWaitPanel = Boolean(account && needsApproval && approvePrimaryPhase !== 'idle')

  const onPrimary = () => {
    if (!account) {
      openConnectModal()
      return
    }
    if (needsApproval) {
      runApprovePrimary()
      return
    }
    openConfirm()
  }

  const percentInputValue = percentValue === '<1' ? '0' : percentValue.replace('%', '')

  return (
    <div className="nm-mobile-simple__convert-stage flex min-h-0 flex-1 flex-col overflow-hidden">
      <MobileSwapScreenHeader
        title={nutrimaticLabels.mobileSimple.removeLiquidityTitle}
        {...liquidityNutrimaticTabletHeaderProps(variant, backToPositions)}
      />

      <div className="nm-mobile-simple__body nm-mobile-simple__body--trade" data-mobile-scroll-container>
        {notice ? <NutrimaticMobileNotice {...notice} /> : null}

        <div className="nm-mobile-simple__trade-stack">
          <section className="nm-mobile-simple__leg nm-mobile-simple__leg--from">
            <div className="nm-mobile-simple__leg-label">{nutrimaticLabels.mobileSimple.removeAmount}</div>
            <div className="nm-mobile-simple__leg-main justify-end">
              {isDesktopTablet ? (
                <input
                  type="text"
                  inputMode="numeric"
                  className={cn(
                    'nm-mobile-simple__amount-input nm-mobile-simple__amount-input--active',
                    percentInputValue === '0' && 'nm-mobile-simple__amount-input--empty',
                  )}
                  value={percentInputValue}
                  placeholder="0"
                  onChange={(event) => applyPercentInput(event.target.value)}
                  aria-label={nutrimaticLabels.mobileSimple.removeAmount}
                />
              ) : (
                <MobileFluidAmount
                  value={`${percentValue}%`}
                  active
                  empty={percentValue === '0'}
                  onFocus={() => undefined}
                  aria-label={nutrimaticLabels.mobileSimple.removeAmount}
                />
              )}
            </div>
          </section>
        </div>

        <p className="nm-mobile-simple__leg-label px-[0.875rem] pt-3">
          {nutrimaticLabels.mobileSimple.youWillReceive}
        </p>

        <div className="nm-mobile-simple__trade-stack mt-1">
          <NutrimaticMobileCurrencyLeg
            value={formattedAmounts[Field.CURRENCY_A]}
            currency={currencyA}
            position="from"
            readOnly
          />

          <NutrimaticMobileCurrencyLeg
            value={formattedAmounts[Field.CURRENCY_B]}
            currency={currencyB}
            position="to"
            readOnly
          />
        </div>

        <MobileRemoveLiquidityMetricsPanel controller={controller} />
      </div>

      <div className={cn('nm-mobile-simple__input-dock', isDesktopTablet && 'nm-mobile-simple__input-dock--desktop')}>
        <div className="nm-mobile-simple__footer">
          {showApproveWaitPanel ? (
            <WalletActionWaitPanel
              phase={approvePrimaryPhase === 'awaiting-confirmation' ? 'awaiting-confirmation' : 'awaiting-wallet'}
              onCancel={canCancelApprovePrimary ? cancelApprovePrimary : undefined}
            />
          ) : (
            <WalletActionButton
              type="button"
              className={cn('nm-mobile-simple__cta', !primaryDisabled && error && hasAmounts && 'bg-destructive')}
              disabled={primaryDisabled}
              loading={primaryLoading}
              onClick={onPrimary}
            >
              {account && error && !needsApproval && hasAmounts ? error : primaryLabel}
            </WalletActionButton>
          )}
        </div>

        <div className="nm-mobile-simple__presets">
          {REMOVE_PRESETS.map((fraction) => {
            const label =
              fraction === 1 ? nutrimaticLabels.mobileSimple.maxPreset : `${Math.round(fraction * 100)}%`
            const isActive = percentValue === String(Math.round(fraction * 100))
            return (
              <button
                key={fraction}
                type="button"
                className={cn('nm-mobile-simple__preset', isActive && 'nm-mobile-simple__preset--active')}
                disabled={!account}
                onClick={() => onUserInput(Field.LIQUIDITY_PERCENT, String(Math.round(fraction * 100)))}
              >
                {label}
              </button>
            )
          })}
        </div>

        {!isDesktopTablet ? <MobileAmountKeypad onKeyPress={applyKeypad} /> : null}
      </div>
    </div>
  )
}

/** Flujo móvil completo (form → review → success). */
export function LiquidityNutrimaticMobileRemoveView({
  controller,
}: {
  controller: RemoveLiquidityControllerResult
}) {
  const { screen } = useLiquidityRemoveNutrimaticFlow(controller)

  if (screen === 'success') {
    return <LiquidityNutrimaticMobileRemoveSuccessView controller={controller} />
  }

  if (screen === 'cancelled') {
    return <LiquidityNutrimaticMobileRemoveCancelledView controller={controller} />
  }

  if (screen === 'error') {
    return <LiquidityNutrimaticMobileRemoveErrorView controller={controller} />
  }

  if (screen === 'review') {
    return <LiquidityNutrimaticMobileRemoveReviewView controller={controller} />
  }

  return <LiquidityNutrimaticMobileRemoveFormView controller={controller} variant="mobile" />
}
